import React from 'react';
import styled from 'styled-components';
import { CampaignRecipientEstimate } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-S);
`;

const StatCard = styled.div<{ $variant: 'success' | 'warning' } & React.HTMLAttributes<HTMLDivElement>>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-XS);
    padding: var(--spacer-M) var(--spacer-S);
    border-radius: var(--spacer-XS);
    background: ${p => p.$variant === 'success' ? 'var(--success-bg)' : 'var(--warning-bg)'};
    border: var(--border-thin) solid ${p => p.$variant === 'success' ? 'var(--success-border)' : 'var(--warning-border)'};
`;

const StatNumber = styled.div<{ $variant: 'success' | 'warning' } & React.HTMLAttributes<HTMLDivElement>>`
    font-size: 2rem;
    font-weight: 800;
    line-height: 1;
    color: ${p => p.$variant === 'success' ? 'var(--success-color)' : 'var(--warning-color)'};
`;

const StatLabel = styled.div`
    font-size: 0.75rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
`;

const BreakdownList = styled.ul`
    margin: 0;
    padding-left: 1.2rem;
    font-size: 0.88rem;
    color: var(--text-muted);
`;

const SampleList = styled.ul`
    margin: var(--spacer-XS) 0 0;
    padding-left: 1.2rem;
    font-size: 0.88rem;
`;

const Disclaimer = styled.div`
    font-size: 0.85rem;
    color: var(--text-muted);
    font-style: italic;
`;

const SampleHeading = styled.div`
    font-size: 0.88rem;
    font-weight: 600;
`;

export interface CampaignAudienceSummaryProps {
    estimate?: CampaignRecipientEstimate;
}

export const CampaignAudienceSummary: React.FC<CampaignAudienceSummaryProps> = ({ estimate }) => {
    const t = useTranslations();

    if (!estimate) {
        return <Disclaimer>{t(TK.campaignAudienceEstimatePending)}</Disclaimer>;
    }

    const excludedRows = [
        { label: t(TK.campaignExcludedNoMarketingConsent), value: estimate.exclusionBreakdown.noMarketingConsent },
        { label: t(TK.campaignExcludedConsentNotSubmitted), value: estimate.exclusionBreakdown.marketingConsentNotSubmitted },
        { label: t(TK.campaignExcludedMissingContactInfo), value: estimate.exclusionBreakdown.missingContactInfo },
        { label: t(TK.campaignExcludedInvalidContactInfo), value: estimate.exclusionBreakdown.invalidContactInfo },
        { label: t(TK.campaignExcludedDuplicateAddress), value: estimate.exclusionBreakdown.duplicateAddress },
        { label: t(TK.campaignExcludedManuallyExcluded), value: estimate.exclusionBreakdown.manuallyExcluded },
    ].filter(row => row.value > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <Disclaimer>{t(TK.campaignAudienceDisclaimer)}</Disclaimer>

            <StatsRow>
                <StatCard $variant="success">
                    <StatNumber $variant="success">{estimate.totalIncluded}</StatNumber>
                    <StatLabel>{t(TK.campaignAudienceIncluded)}</StatLabel>
                </StatCard>
                <StatCard $variant="warning">
                    <StatNumber $variant="warning">{estimate.totalExcluded}</StatNumber>
                    <StatLabel>{t(TK.campaignAudienceExcluded)}</StatLabel>
                </StatCard>
            </StatsRow>

            {excludedRows.length > 0 && (
                <BreakdownList>
                    {excludedRows.map(row => (
                        <li key={row.label}>{row.label}: {row.value}</li>
                    ))}
                </BreakdownList>
            )}

            {estimate.samples.length > 0 && (
                <div>
                    <SampleHeading>{t(TK.campaignSampleRecipients)}:</SampleHeading>
                    <SampleList>
                        {estimate.samples.map(sample => (
                            <li key={`${sample.email}-${sample.clientId ?? 'manual'}`}>
                                {sample.clientName ?? sample.email} - {sample.email}
                            </li>
                        ))}
                    </SampleList>
                </div>
            )}
        </div>
    );
};
